import { useEffect, useState, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Package, ArrowLeft, Loader2, ShieldAlert, ExternalLink, Mail } from 'lucide-react';
import { getOrderBySessionId } from '../../services/firestore/orderService';
import { trackFunnelEvent } from '../../services/analytics/funnelTracker';
import { registerCheckoutPromise } from '../../services/firestore/promiseService';
import type { Order } from '../../types/order';
import { PRODUCT_NAME } from '../../config/brand';

export function OrderSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setError('No session ID found');
      setLoading(false);
      return;
    }

    let attempts = 0;
    const maxAttempts = 10;

    // Poll for order (webhook may take a moment)
    const poll = async () => {
      try {
        const found = await getOrderBySessionId(sessionId);
        if (found && found.status !== 'pending') {
          setOrder(found);
          setLoading(false);
          return;
        }
        // Also show pending orders after a few attempts
        if (found && attempts >= 3) {
          setOrder(found);
          setLoading(false);
          return;
        }
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          // Show whatever we have
          if (found) setOrder(found);
          setLoading(false);
        }
      } catch {
        setError('Failed to load order');
        setLoading(false);
      }
    };

    poll();
  }, [sessionId]);

  // Track purchase complete + register shipping promise when order loads
  useEffect(() => {
    if (order && !trackedRef.current) {
      trackedRef.current = true;
      trackFunnelEvent({
        event: 'PURCHASE_COMPLETE',
        metadata: {
          orderId: order.id,
          totalCents: order.totalInCents,
          itemCount: order.lineItems.length,
        },
      });
      // Register shipping promise — fire-and-forget
      registerCheckoutPromise(
        order.id,
        order.customerEmail,
        order.bundleName,
      ).catch(() => {
        // Non-blocking: promise registration failure should not affect order confirmation
      });
    }
  }, [order]);

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-deep flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-accent-light mx-auto mb-4" />
          <p className="text-text-secondary">Confirming your order...</p>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="min-h-screen bg-bg-deep flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <p className="text-text-secondary mb-4">{error || 'Order not found'}</p>
          <Link to="/" className="text-accent-light hover:underline">
            Return home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-deep text-text-primary">
      <div className="max-w-lg mx-auto px-4 py-12">
        {/* Success header */}
        <div className="text-center mb-8">
          <CheckCircle className="w-16 h-16 text-optimal mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold mb-2">Order Confirmed</h1>
          <p className="text-text-secondary">
            Thank you! Your protocol is being prepared.
          </p>
        </div>

        {/* Email confirmation notice */}
        <div className="bg-bg-card border border-bg-tertiary rounded-xl p-4 mb-6 flex items-start gap-3">
          <Mail size={18} className="text-accent-light flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-text-primary">Confirmation email sent</p>
            <p className="text-text-muted mt-0.5">
              We sent your order details to <span className="font-medium text-text-secondary">{order.customerEmail}</span>. Check your inbox (and spam folder, just in case).
            </p>
          </div>
        </div>

        {/* Order details card */}
        <div className="bg-bg-card border border-bg-tertiary rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Package size={18} className="text-gold" />
            <h2 className="font-display text-lg font-bold">Order Details</h2>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-text-muted">Order ID</span>
              <span className="font-mono font-medium">{order.id}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-text-muted">Status</span>
              <span className="text-optimal font-medium capitalize">{order.status}</span>
            </div>
            {order.bundleName && (
              <div className="flex justify-between">
                <span className="text-text-muted">Protocol</span>
                <span>{order.bundleName}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-text-muted">Items</span>
              <span>{order.lineItems.length} supplements</span>
            </div>
            <div className="flex justify-between border-t border-bg-tertiary pt-3">
              <span className="text-text-muted">Total</span>
              <span className="text-accent-light font-bold text-base">
                ${(order.totalInCents / 100).toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        {/* Shipping info */}
        {order.shippingAddress && (
          <div className="bg-bg-card border border-bg-tertiary rounded-xl p-6 mb-6">
            <h3 className="font-display text-sm font-bold mb-3 text-text-muted uppercase tracking-wider">
              Ships To
            </h3>
            <p className="text-sm leading-relaxed">
              {order.customerName && <span className="block font-medium">{order.customerName}</span>}
              {order.shippingAddress.line1}
              {order.shippingAddress.line2 && <>, {order.shippingAddress.line2}</>}
              <br />
              {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postal_code}
            </p>
          </div>
        )}

        {/* Next steps */}
        <div className="bg-bg-elevated/50 border border-bg-tertiary rounded-xl p-6 mb-6">
          <h3 className="font-display text-sm font-bold mb-3">What happens next</h3>
          <ul className="space-y-2 text-sm text-text-secondary">
            <li className="flex items-start gap-2">
              <span className="text-gold mt-0.5">1.</span>
              Your confirmation email is on its way now
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gold mt-0.5">2.</span>
              Our team reviews and prepares your supplements (3–5 business days)
            </li>
            <li className="flex items-start gap-2">
              <span className="text-gold mt-0.5">3.</span>
              You'll receive a shipping email with your tracking number
            </li>
          </ul>
        </div>

        {/* Adverse reaction safety notice */}
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-2 mb-3">
            <ShieldAlert size={18} className="text-red-700 flex-shrink-0" />
            <h3 className="font-display text-sm font-bold text-red-900">Important Safety Information</h3>
          </div>
          <p className="text-[13px] text-red-800 font-semibold mb-3">
            If you are experiencing a medical emergency, call 911.
          </p>
          <p className="text-[13px] text-zinc-700 leading-relaxed mb-4">
            If you experience any adverse reaction to a supplement, <strong className="text-zinc-900">discontinue use immediately</strong> and
            contact your healthcare provider. Then let us know so we can update your profile and investigate.
          </p>
          <div className="flex flex-col gap-2">
            <Link
              to="/support#adverse-reaction"
              className="inline-flex items-center gap-2 text-[13px] font-semibold text-accent-light hover:underline"
            >
              <ShieldAlert size={14} />
              Report an adverse reaction
            </Link>
            <a
              href="mailto:safety@hemologix.ai"
              className="inline-flex items-center gap-2 text-[13px] font-medium text-zinc-600 hover:text-zinc-900 hover:underline"
            >
              Or email safety@hemologix.ai directly
            </a>
            <a
              href="https://www.safetyreporting.hhs.gov"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[13px] font-medium text-zinc-500 hover:text-zinc-700 hover:underline"
            >
              <ExternalLink size={12} />
              File a report with FDA MedWatch
            </a>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3">
          <Link
            to="/order/lookup"
            className="block text-center px-6 py-3 bg-accent-light/10 border border-accent-light/30 text-accent-light rounded-xl font-medium hover:bg-accent-light/20 transition-colors"
          >
            Track Your Order
          </Link>
          <Link
            to="/"
            className="flex items-center justify-center gap-2 text-text-muted hover:text-text-primary transition-colors py-2"
          >
            <ArrowLeft size={16} />
            Back to {PRODUCT_NAME}
          </Link>
        </div>
      </div>
    </div>
  );
}
